/**
 * Admin — Pending Payments.
 *
 * Every payment the frontend initiates (PayFast checkout redirect for
 * credit purchases or activation renewals) creates a `pending_payments`
 * row on the backend before we hand the buyer off to the gateway. When
 * PayFast fires the ITN webhook, we settle the row and mark it
 * `completed`. If anything goes wrong (signature mismatch, wrong
 * amount, network hiccup while validating), the row is stamped with a
 * failure status and this page lets the admin review + force-settle
 * cases where the buyer paid but the webhook didn't reach us.
 */
import React, { useEffect, useState } from "react";
import { listPendingPayments, forceSettlePayment } from "../lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  Wallet,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  Zap,
} from "lucide-react";

const STATUS_STYLES = {
  pending:            "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
  completed:          "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  signature_failed:   "bg-red-500/10 text-red-300 border-red-500/30",
  validate_failed:    "bg-red-500/10 text-red-300 border-red-500/30",
  amount_mismatch:    "bg-red-500/10 text-red-300 border-red-500/30",
  settle_failed:      "bg-red-500/10 text-red-300 border-red-500/30",
  CANCELLED:          "bg-neutral-500/10 text-neutral-300 border-neutral-500/30",
  FAILED:             "bg-red-500/10 text-red-300 border-red-500/30",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-neutral-500/10 text-neutral-300 border-neutral-500/30";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${cls}`}>
      {status || "unknown"}
    </span>
  );
}

const PURPOSE_LABEL = {
  renew: "Renewal",
  credits: "Reseller credits",
  activation: "New activation",
};

export default function PendingPayments() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listPendingPayments();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast({
        title: "Failed to load pending payments",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const forceSettle = async (row) => {
    if (!window.confirm(
      `Force-settle payment ${row.m_payment_id}? This will grant ${row.purpose === "credits" ? "credits" : "renewal"} as if the payment succeeded — only do this if you've verified the funds arrived in PayFast.`,
    )) return;
    setBusyId(row.m_payment_id);
    try {
      await forceSettlePayment(row.m_payment_id);
      toast({
        title: "Settled",
        description: `${row.m_payment_id} marked completed and side-effects applied.`,
      });
      await load();
    } catch (e) {
      toast({
        title: "Force-settle failed",
        description: e?.response?.data?.detail || e?.message,
        variant: "destructive",
      });
    } finally {
      setBusyId(null);
    }
  };

  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-pending-payments-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 text-2xl font-bold">
            <Wallet className="h-7 w-7 text-emerald-400" />
            Pending payments
          </h1>
          <p className="max-w-2xl text-sm text-neutral-400">
            Every PayFast checkout is recorded here before the buyer is
            redirected. When PayFast fires the ITN webhook, the row is
            auto-settled. Use <strong>Force settle</strong> only when the
            buyer paid but the webhook didn&apos;t arrive (rare — usually
            an IP allowlist or DNS issue).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="pending-reload">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Reload
          </Button>
        </div>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card className="border-white/10 bg-neutral-900">
          <CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Pending</div>
            <div className="mt-1 flex items-center gap-2 text-xl font-bold text-yellow-300">
              <Clock className="h-4 w-4" /> {counts.pending || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="border-white/10 bg-neutral-900">
          <CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Completed</div>
            <div className="mt-1 flex items-center gap-2 text-xl font-bold text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> {counts.completed || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="border-white/10 bg-neutral-900">
          <CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Failed checks</div>
            <div className="mt-1 flex items-center gap-2 text-xl font-bold text-red-300">
              <AlertCircle className="h-4 w-4" /> {(counts.signature_failed || 0) + (counts.validate_failed || 0) + (counts.amount_mismatch || 0) + (counts.settle_failed || 0)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-white/10 bg-neutral-900">
          <CardContent className="p-4">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Total</div>
            <div className="mt-1 flex items-center gap-2 text-xl font-bold text-white">
              <Zap className="h-4 w-4" /> {rows.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-white/10 bg-neutral-900">
        <CardHeader>
          <CardTitle className="text-base">All payments</CardTitle>
          <CardDescription className="text-xs text-neutral-500">
            Newest first. Amounts shown as source (USD) and ZAR after FX conversion.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <p className="text-sm text-neutral-500" data-testid="pending-loading">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-neutral-500" data-testid="pending-empty">
              No payments recorded yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="pending-table">
                <thead>
                  <tr className="border-b border-white/5 text-[11px] uppercase tracking-widest text-neutral-500">
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3">Purpose</th>
                    <th className="py-2 pr-3">Reference</th>
                    <th className="py-2 pr-3">Amount</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.m_payment_id}
                      className="border-b border-white/5 last:border-0"
                      data-testid={`pending-row-${r.m_payment_id}`}
                    >
                      <td className="py-3 pr-3 text-xs text-neutral-400">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 pr-3 text-xs">
                        <div className="text-white">{PURPOSE_LABEL[r.purpose] || r.purpose}</div>
                        {r.activation_key && (
                          <div className="font-mono text-[10px] text-neutral-500">
                            key: {r.activation_key}
                          </div>
                        )}
                        {r.order_id && (
                          <div className="font-mono text-[10px] text-neutral-500">
                            order: {r.order_id.slice(0, 8)}
                          </div>
                        )}
                        {r.plan_id && (
                          <div className="text-[10px] text-neutral-500">
                            plan: {r.plan_id}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3 font-mono text-[11px] text-neutral-400">
                        {r.m_payment_id}
                      </td>
                      <td className="py-3 pr-3 text-xs">
                        <div className="text-white">
                          ZAR {typeof r.zar_amount === "number" ? r.zar_amount.toFixed(2) : r.zar_amount}
                        </div>
                        <div className="text-[10px] text-neutral-500">
                          {r.source_currency} {r.source_amount} · fx {r.fx_rate?.toFixed(2)} ({r.fx_source})
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <StatusBadge status={r.status} />
                        {r.force_settled_by && (
                          <div className="mt-1 text-[10px] text-neutral-500">
                            forced by {r.force_settled_by}
                          </div>
                        )}
                        {r.error && (
                          <div className="mt-1 max-w-[220px] truncate text-[10px] text-red-400" title={r.error}>
                            {r.error}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-right">
                        {r.status !== "completed" && r.purpose !== "activation" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busyId === r.m_payment_id}
                            onClick={() => forceSettle(r)}
                            className="border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10"
                            data-testid={`pending-force-settle-${r.m_payment_id}`}
                          >
                            {busyId === r.m_payment_id ? "…" : "Force settle"}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
